import { useContext } from 'react';
import { IconButton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Gear } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ActivityInstance } from '@api';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { useRoutines } from '@/contexts/RoutineContext';

/**
 * Kugghjulet som öppnar rutinen för ändring, från dag- och veckovyn.
 *
 * Ligger PÅ kortet i stället för att hela kortet leder till redigeringen: kortet ska
 * fortsätta göra det ett kort gör — öppna aktiviteten — och ändringen är en egen
 * handling som förtjänar en egen liten yta. Samma hållning som krysset bredvid.
 *
 * Renderar ingenting utanför handledarläge med vald brukare, och ingenting för en
 * aktivitet som inte är en rutin: en vanlig aktivitet ändras i appens vanliga
 * redigering, medan rutinen behöver formuläret som kan stegens tider.
 */

type EditRoutineButtonProps = {
  instance: ActivityInstance;
  /** Placering/storlek hos anroparen (kortets hörn, radens slut). */
  sx?: SxProps<Theme>;
  /** Ikonens storlek i px. */
  size?: number;
};

const EditRoutineButton = ({ instance, sx, size = 16 }: EditRoutineButtonProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSupervising, currentClient } = useContext(SupervisorContext);
  const { getRoutine } = useRoutines();

  const routine = getRoutine(instance);
  if (!isSupervising || !currentClient || !routine) return null;

  const handleClick = (event: React.MouseEvent) => {
    // Kortet/raden under kugghjulet navigerar till aktiviteten — utan detta hade
    // trycket både öppnat aktiviteten och redigeringen.
    event.stopPropagation();
    navigate(`/supervisor/routine/${routine.activityId}`);
  };

  return (
    <IconButton
      onClick={handleClick}
      aria-label={t('activity.editRoutine', { name: instance.activityName }) as string}
      size="small"
      sx={{ color: 'text.secondary', p: 0.5, ...sx }}
    >
      <Gear size={size} weight="bold" />
    </IconButton>
  );
};

export default EditRoutineButton;
